import React, { useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, type IconName, usePressableState } from '@garnish/core';

import { useStripePaymentMethod } from '../../hooks/useStripePaymentMethod';
import { type PaymentMethod } from '../../types';
import { removeEndingIn } from '../../utils';
import { StripePaymentMethodImage } from '../StripePaymentMethodImage';

// ─────────────────────────────────────────────────────────────────────────────

export const StripePaymentMethod = (props: StripePaymentMethodProps) => {
  const {
    hasStripeFailedToLoad,
    paymentMethods,
    name,
    imageBase64,
    icon,
    isAddingNewPaymentMethod,
    isDisabled,
    isGooglePay,
    onPress,
  } = props;

  const { formatMessage } = useIntl();
  const { ref, linkStyles } = usePaymentMethodLink(isDisabled);

  const { paymentMethod, paymentMethodIcon, hasNoPaymentMethod } = useStripePaymentMethod({
    hasStripeFailedToLoad,
    paymentMethods,
    name,
    icon,
    isAddingNewPaymentMethod,
    isGooglePay,
  });

  return (
    <View style={styles.messageWrapper}>
      {hasNoPaymentMethod ? null : (
        <BodyText style={styles.message} sizeMatch={['24']}>
          <FormattedMessage {...messages.payWith} />
        </BodyText>
      )}

      <Pressable
        ref={ref}
        style={styles.linkWrapper}
        disabled={isDisabled}
        accessibilityRole="button"
        accessibilityLabel={formatMessage(messages.changePaymentMethod)}
        onPress={onPress}
      >
        <StripePaymentMethodImage imageBase64={imageBase64} icon={paymentMethodIcon} />

        <BodyText numberOfLines={1} ellipsizeMode="tail" sizeMatch={['24']} style={linkStyles}>
          {removeEndingIn(paymentMethod)}
        </BodyText>
      </Pressable>
    </View>
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const ICON_SIZE = 32;

// ─── Helpers ────────────────────────────────────────────────────────────────

const usePaymentMethodLink = (isDisabled: boolean) => {
  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);

  const linkStyles = [
    styles.message,
    styles.link,
    isInteracting && styles.linkInteracting,
    isDisabled && styles.linkDisabled,
  ];

  return { ref, linkStyles };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  payWith: {
    defaultMessage: 'Pay with',
    description: 'Bag | Payment method | Hint',
  },
  changePaymentMethod: {
    defaultMessage: 'Change payment method',
    description: 'Bag | Selected Payment Method | Select another',
  },
  stripeFailedToLoad: {
    defaultMessage: 'Something went wrong.',
    description: 'Bag | Selected Payment Method | Stripe failed to load',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  messageWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    marginRight: theme.spacing['8'],
    paddingVertical: theme.spacing['4'],
  },
  message: {
    lineHeight: ICON_SIZE,
  },
  linkWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
    borderBottomColor: theme.colors.SPINACH,
    borderBottomWidth: 2,
  },
  link: {
    color: theme.colors.SPINACH,
  },
  linkInteracting: {
    color: theme.colors.KALE_HOVER,
  },
  linkDisabled: {
    color: theme.colors.GRAY,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type StripePaymentMethodProps = Readonly<{
  hasStripeFailedToLoad: boolean;
  paymentMethods: readonly PaymentMethod[];
  name?: string;
  imageBase64?: string;
  icon?: IconName;
  isAddingNewPaymentMethod: boolean;
  isGooglePay: boolean;
  isDisabled: boolean;
  onPress: () => void;
}>;
